import type { CertificationDocument } from 'src/types/CertificationDocumentTypes';

const documentMatchesSearch = (
  doc: CertificationDocument,
  needle: string
): boolean => {
  const titleMatch = doc.title?.toLowerCase().includes(needle);
  const categoryMatch = doc.category?.toLowerCase().includes(needle);
  return Boolean(titleMatch || categoryMatch);
};

export const filterDocumentsBySearch = (
  documents: CertificationDocument[] | null | undefined,
  term: string | null | undefined
): CertificationDocument[] => {
  if (!Array.isArray(documents)) {
    return [];
  }

  const trimmed = term?.trim();
  if (!trimmed) {
    return documents;
  }

  const needle = trimmed.toLowerCase();
  return documents
    .map((doc) => {
      const parentMatches = documentMatchesSearch(doc, needle);

      // Filter upper children
      const matchingUpperChildren = (
        doc.upper_children_certification_documents || []
      ).filter((child) => documentMatchesSearch(child, needle));

      // Filter lower children
      const matchingLowerChildren = (
        doc.lower_children_certification_documents || []
      ).filter((child) => documentMatchesSearch(child, needle));

      // If parent matches, include it with all children
      if (parentMatches) {
        return doc;
      }

      // If any children match, include parent with only matching children
      if (
        matchingUpperChildren.length > 0 ||
        matchingLowerChildren.length > 0
      ) {
        return {
          ...doc,
          upper_children_certification_documents: matchingUpperChildren,
          lower_children_certification_documents: matchingLowerChildren,
        };
      }

      // Nothing matches, exclude this parent
      return null;
    })
    .filter((doc): doc is CertificationDocument => doc !== null);
};

export const applyDisplayOrder = (
  documents: CertificationDocument[]
): CertificationDocument[] => {
  let counter = 0;
  return (documents || []).map((doc) => {
    const clone = { ...doc };
    if (clone.parent == null) {
      clone.displayOrder = counter++;
    } else if ('displayOrder' in clone) {
      delete clone.displayOrder;
    }
    return clone;
  });
};

const documentMatchesProgram = (
  doc: CertificationDocument,
  programs: string[]
): boolean => {
  const label =
    typeof doc.tic_setup === 'string' && doc.tic_setup.trim().length > 0
      ? doc.tic_setup.trim()
      : '';
  return programs.includes(label);
};

export const applyFilters = (
  documents: CertificationDocument[],
  search: string | null,
  selectedPrograms: string[]
): CertificationDocument[] => {
  if (!Array.isArray(documents)) {
    return [];
  }

  const textFiltered = filterDocumentsBySearch(documents, search);

  const normalizedPrograms =
    (selectedPrograms || []).map((p) =>
      typeof p === 'string' ? p : p ?? ''
    ) ?? [];

  const programFiltered =
    normalizedPrograms.length > 0
      ? textFiltered
          .map((doc) => {
            const parentMatches = documentMatchesProgram(
              doc,
              normalizedPrograms
            );

            // Filter upper children
            const matchingUpperChildren = (
              doc.upper_children_certification_documents || []
            ).filter((child) =>
              documentMatchesProgram(child, normalizedPrograms)
            );

            // Filter lower children
            const matchingLowerChildren = (
              doc.lower_children_certification_documents || []
            ).filter((child) =>
              documentMatchesProgram(child, normalizedPrograms)
            );

            // If parent matches, include it with all children
            if (parentMatches) {
              return doc;
            }

            // If any children match, include parent with only matching children
            if (
              matchingUpperChildren.length > 0 ||
              matchingLowerChildren.length > 0
            ) {
              return {
                ...doc,
                upper_children_certification_documents: matchingUpperChildren,
                lower_children_certification_documents: matchingLowerChildren,
              };
            }

            // Nothing matches, exclude this parent
            return null;
          })
          .filter((doc): doc is CertificationDocument => doc !== null)
      : textFiltered;

  return programFiltered;
};
